import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import moment from 'moment-timezone';
import './style.scss';
import SelectableListItem from 'components/Utils/SelectableListItem';
import List from '@material-ui/core/List';
import CalendarUtils from 'services/CalendarUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import _ from 'lodash';
import Icon from 'components/Utils/NewIcon';
import StaffUtils from 'services/StaffUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import MenuItem from '@material-ui/core/MenuItem';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import GenericBadge from 'components/Utils/GenericBadge';
import PhysicianUtils from 'services/PhysicianUtils';
import DefaultModal from 'components/Utils/DefaultModal';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import PatientSearchField from 'components/Patient/PatientSearchField';
import IconButton from '@material-ui/core/IconButton';
import useFetchPatients from 'hooks/useFetchPatients';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import { ShiftSchedule } from 'components/Schedule/Vacations';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import UnreadBadge from 'components/Common/UnreadBadge';
import ShiftUtils from 'services/ShiftUtils';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import { Comments } from 'components/Tasks/TaskDetails/view';
import TaskUtils from 'services/TaskUtils';
import PatientUtils from 'services/PatientUtils';
import ResponsiveColumns from 'components/ResponsiveColumns';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';

const DEFAULT_ARRAY = [];

export const EditHandovers = ({ onClose, patientId, hideTitle = false }) => {
  const [openHandover, setOpenHandover] = useState();
  const handovers = useAppSelector((state) => state.shifts.handovers);
  const ownId = useAppSelector((state) => state.authentication.user.id);

  const filteredHandovers = useMemo(() => {
    return _.sortBy(
      handovers.filter((handover) => handover.physician_id === ownId),
      'date_start',
    ).reverse();
  }, [handovers, ownId]);

  return (
    <>
      <div className="request-options underside-right-panel">
        {onClose && (
          <div className="close">
            <Icon src="xmark" onClick={onClose} />
          </div>
        )}
        {!hideTitle && <span className="large-title padded-title">Handovers</span>}
        <DefaultButton
          name="Add New Handover"
          icon="user-group"
          className="blue d-block"
          style={{ width: 'calc(100% - 50px)', margin: '15px 25px' }}
          onClick={() => {
            setOpenHandover({});
          }}
        />
        <List>
          {filteredHandovers.length > 0 ? (
            filteredHandovers.map((handover) => (
              <HandoverBadge
                key={handover.id}
                handover={handover}
                onClick={() => setOpenHandover(handover)}
              />
            ))
          ) : (
            <div className="not-found">
              <span>No handovers found</span>
            </div>
          )}
        </List>
      </div>
      {!!openHandover && (
        <DefaultModal
          className="handover-modal"
          title="Edit Handover"
          wrap={true}
          padding={false}
          content={
            <HandoverDetails
              key={openHandover.id}
              patientId={patientId}
              handover={openHandover}
              onClose={() => setOpenHandover()}
            />
          }
          isOpen={!!openHandover}
          onRequestClose={() => setOpenHandover()}
        />
      )}
    </>
  );
};

let messagesEnd;

function scrollToBottom() {
  if (!!messagesEnd) messagesEnd.scrollIntoView();
}

const GeneralCommentSection = 'General';

/**
 * @param {{
 * handover: import("generated/api").PatientHandover
 *
 * }} args
 */
export const HandoverDetails = ({
  handover,
  onClose,
  editable = true,
  archivable = false,
  patientId,
}) => {
  const dispatch = useAppDispatch();
  const physicians = useAppSelector((state) => state.physicians.byId);
  const patients = useAppSelector((state) => state.patients.byId);
  const shifts = useAppSelector((state) => state.shifts.shifts);
  const ownId = useAppSelector((state) => state.authentication.user.id);

  const [date, setDate] = useState(moment().unix());
  const [value, setValue] = useState({ ...handover });
  const [commentSection, setCommentSection] = useState({
    name: GeneralCommentSection,
  });
  const saveTimeoutRef = useRef(null);
  const commentSections = useMemo(() => {
    const res =
      handover.patients
        ?.map((it) => {
          return it?.id && { name: PatientUtils.getName(patients[it.id]), id: it.id };
        })
        ?.filter(Boolean) || [];
    return [{ name: GeneralCommentSection }, ...res];
  }, [handover, patients]);

  const physician = physicians[ownId];
  const clinicId = physician.clinic_id;

  const loadingSave = useIsLoading(api.Shifts.addPatientHandover);
  const loadingDelete = useIsLoading(api.Shifts.removePatientHandover);

  const patientsToFetch =
    value.patients?.filter(({ id }) => id).map((info) => ({ patient_id: info.id })) ||
    DEFAULT_ARRAY;

  const loadingPatients = useFetchPatients(patientsToFetch);

  // Debounced save function with conflict resolution
  const debouncedSave = useCallback(
    _.debounce(async (dataToSave) => {
      try {
        const toSave = {
          ...dataToSave,
          physician_id: dataToSave.physician_id || ownId,
          clinic_id: dataToSave.clinic_id || clinicId,
          patients:
            dataToSave.patients?.filter(
              ({ id, description, assigned_to_id }) =>
                !!id || !!description?.trim() || !!assigned_to_id,
            ) || [],
        };

        const result = await dispatch(
          api.Shifts.addPatientHandover(
            { patientHandover: toSave },
            { errorTitle: 'Save Handover' },
          ),
        );

        if (result) {
          // Only update state if no newer local changes exist
          setValue((prev) => {
            // Check if user made changes while saving by comparing key fields
            const hasNewerChanges =
              prev.patients?.length !== result.patients?.length ||
              prev.assigned_to_id !== result.assigned_to_id ||
              prev.date_start !== result.date_start ||
              prev.date_end !== result.date_end;

            if (hasNewerChanges) {
              // Preserve local changes, but update server fields like updated_at, id
              return {
                ...prev,
                id: result.id,
                updated_at: result.updated_at,
                // Preserve any server-only fields that might have changed
                ...Object.fromEntries(
                  Object.entries(result).filter(
                    ([key]) =>
                      ![
                        'patients',
                        'assigned_to_id',
                        'date_start',
                        'date_end',
                        'comments',
                      ].includes(key),
                  ),
                ),
              };
            }
            // No local changes, use server result
            return result;
          });
        }
      } catch (error) {
        console.error('Handover save failed:', error);
      }
    }, 500),
    [dispatch, ownId, clinicId],
  );

  // Optimistic save function
  const saveChange = useCallback(
    (key, keyValue) => {
      const updatedValue = { ...value, [key]: keyValue };
      setValue(updatedValue);

      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Trigger debounced save
      debouncedSave(updatedValue);

      // Return a resolved promise for compatibility with existing code
      return Promise.resolve(true);
    },
    [value, debouncedSave],
  );

  const navigateToColumn = useRef();

  const unreadComments = useMemo(
    () => ({
      [GeneralCommentSection]: TaskUtils.getUnreadComments(value, ownId),
      ..._(value.patients)
        .groupBy('id')
        .mapValues((it) => TaskUtils.getUnreadComments(it[0], ownId))
        .value(),
    }),
    [value, ownId],
  );

  useEffect(() => {
    // Only mark comments as read when in non-editable mode (when comments are visible)
    if (editable) {
      return;
    }

    const unread = unreadComments[commentSection.id || commentSection.name];

    if (unread === 0) {
      return;
    }

    const isGeneralComments = commentSection.name === GeneralCommentSection;

    let originalComments = value.comments;
    if (!isGeneralComments) {
      originalComments = value.patients?.find((it) => it.id === commentSection.id)?.comments;
    }
    const newComments = originalComments.map((comment) => ({
      ...comment,
      read_by: _.uniq([...(comment.read_by || []), ownId]),
    }));

    if (isGeneralComments) {
      saveChange('comments', newComments);
    } else {
      const idx = value.patients.findIndex((it) => it.id === commentSection.id);
      if (idx !== -1) {
        const newPatients = [
          ...value.patients.slice(0, idx),
          { ...value.patients[idx], comments: newComments },
          ...value.patients.slice(idx + 1),
        ];
        saveChange('patients', newPatients);
      }
    }
  }, [editable, ownId, unreadComments, value, commentSection, saveChange]);

  useEffect(() => {
    scrollToBottom();
  }, [value.comments]);

  // Cleanup debounced save on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  function getDateShift(shifts, dateMoment) {
    return shifts.find((shift) => CalendarUtils.isSameDay(moment.unix(shift.start), dateMoment));
  }
  function findShifts(shifts, startUnix, increment = 1) {
    let foundShifts = [];
    let startMoment = moment.unix(startUnix);
    let shift = getDateShift(shifts, startMoment);
    if (shift?.non_working) {
      foundShifts.push(shift);
      //this is a valid vacation shift
      let keepLooking = true;
      let startDate = startMoment.clone();
      do {
        startDate.add(increment, 'day');
        let foundShift = getDateShift(shifts, startDate);
        if (foundShift && !foundShift.non_working) {
          keepLooking = false;
        } else {
          if (foundShift) {
            foundShifts.push(foundShift);
          }
        }
      } while (keepLooking);
    }
    return foundShifts;
  }

  function onSelectSlot({ start, end, slots }) {
    const userShifts = shifts.filter((shift) => shift.user_id === ownId);
    let date = slots[0].unix();

    let shiftsAfter = findShifts(userShifts, date, 1);
    let shiftsBefore = findShifts(userShifts, date, -1);

    let found = _.sortBy(_.uniqBy([...shiftsAfter, ...shiftsBefore], 'id'), 'start');

    if (found.length > 0 && editable) {
      setValue({
        ...value,
        date_start: found[0].start,
        date_end: found[found.length - 1].start,
      });
    }
  }

  let activePhysicians = useMemo(() => {
    return StaffUtils.filterActiveUsers(PerformanceUtils.objectToArray(physicians)).filter((user) =>
      PhysicianUtils.isNursePractitioner(user),
    );
  }, [physicians]);

  function change(key, val) {
    if (!editable) return;
    saveChange(key, val);
  }

  function changePatient(index, data) {
    let patients = [...(value.patients || [])];
    patients[index] = data;
    change('patients', patients);
  }

  function removePatient(index) {
    let patients = [...(value.patients || [])];
    patients.splice(index, 1);
    change('patients', patients);
  }

  async function onSave(shouldClose = true, updatedValue = value) {
    // Cancel any pending debounced save
    debouncedSave.cancel();

    let toSave = {
      ...updatedValue,
      physician_id: updatedValue.physician_id || ownId,
      clinic_id: updatedValue.clinic_id || clinicId,
    };
    toSave.patients = toSave.patients?.filter(
      ({ id, description, assigned_to_id }) => !!id || !!description?.trim() || !!assigned_to_id,
    );

    try {
      let result = await dispatch(
        api.Shifts.addPatientHandover({ patientHandover: toSave }, { errorTitle: 'Save Handover' }),
      );

      if (result) {
        setValue(result);
        if (shouldClose) onClose();
      }
      return result;
    } catch (error) {
      console.error('Manual save failed:', error);
      return null;
    }
  }

  async function onDelete() {
    await dispatch(
      api.Shifts.removePatientHandover({ id: value.id }, { errorTitle: 'Delete Handover' }),
    );
    onClose();
  }

  async function onArchive() {
    return saveChange('archived', true);
  }

  const saveComment = (commentSection) => async (text, notification) => {
    const isGeneralCommentSection = commentSection.name === GeneralCommentSection;
    let originalComments = value.comments;

    if (!isGeneralCommentSection) {
      originalComments = value.patients?.find((it) => it.id === commentSection.id)?.comments;
    }

    let data = {
      author_id: ownId,
      text,
      date: moment().unix(),
      read_by: [ownId],
      notifications_disabled: !notification,
    };
    let newComments = [...(originalComments || []), data];

    if (isGeneralCommentSection) {
      return saveChange('comments', newComments);
    } else {
      const idx = value.patients.findIndex((it) => it.id === commentSection.id);
      if (idx !== -1) {
        const newPatients = [
          ...value.patients.slice(0, idx),
          { ...value.patients[idx], comments: newComments },
          ...value.patients.slice(idx + 1),
        ];
        return saveChange('patients', newPatients);
      }
    }
  };

  const deleteComment = (commentSection) => async (comment) => {
    const isGeneralCommentSection = commentSection.name === GeneralCommentSection;
    let originalComments = value.comments;

    if (!isGeneralCommentSection) {
      originalComments = value.patients?.find((it) => it.id === commentSection.id)?.comments;
    }

    const newComments = originalComments.filter(
      ({ author_id, text, date }) =>
        author_id !== comment.author_id || text !== comment.text || date !== comment.date,
    );

    if (isGeneralCommentSection) {
      return saveChange('comments', newComments);
    } else {
      const idx = value.patients.findIndex((it) => it.id === commentSection.id);
      if (idx !== -1) {
        const newPatients = [
          ...value.patients.slice(0, idx),
          { ...value.patients[idx], comments: newComments },
          ...value.patients.slice(idx + 1),
        ];
        return saveChange('patients', newPatients);
      }
    }
  };

  function setMessagesEnd(element) {
    messagesEnd = element;
  }

  const buttonDisabled = !value.assigned_to_id || !value.date_start || !value.date_end;

  let width = '515px';
  if (value.patients?.length > 0) width = '845px';

  const quickAddPatient = useMemo(() => {
    return patientId && !value.patients?.find(({ id }) => id === patientId);
  }, [value.patients, patientId]);

  return (
    <div className="handover-details">
      <ResponsiveColumns
        navigateTo={navigateToColumn}
        columns={[
          {
            name: 'Details',
            element: (
              <>
                <div className="fields" style={{ width }}>
                  {!value.date_start && (
                    <div className="explanation">
                      <span>
                        Select a <span className="italic">Paid Time Off</span> by clicking on the
                        corresponding date in the calendar.
                      </span>
                    </div>
                  )}
                  {!editable && <div className="small-title">Details</div>}
                  <div className="row" key={value.date_start}>
                    <DefaultDatePicker
                      key={'start' + value.date_start}
                      name="date_start"
                      label="Date Start"
                      value={value.date_start}
                      onChange={(e, val) => change('date_start', val)}
                      disabled={!editable}
                    />
                    <DefaultDatePicker
                      key={'end' + value.date_end}
                      name="date_end"
                      label="Date End"
                      value={value.date_end}
                      onChange={(e, val) => change('date_end', val)}
                      disabled={!editable}
                    />
                    <DefaultSelectField
                      style={{ width: '100%' }}
                      name="assigned_to_id"
                      label="Assign To"
                      disabled={!editable}
                      value={value.assigned_to_id}
                      onChange={(event) => change('assigned_to_id', event.target.value)}
                      inputElements={activePhysicians.map((physician) => {
                        return (
                          <MenuItem value={physician.id} key={physician.id}>
                            {PhysicianUtils.getName(physician)}
                          </MenuItem>
                        );
                      })}
                    />
                  </div>
                  {quickAddPatient && (
                    <DefaultButton
                      name={`Add ${PatientUtils.getName(
                        patients[patientId],
                        undefined,
                        false,
                        false,
                      )}`}
                      style={{ width: '250px', marginLeft: '10px' }}
                      className="blue"
                      icon="user-plus"
                      onClick={() => {
                        changePatient(value.patients?.length || 0, {
                          assigned_to_id: value.assigned_to_id,
                          id: patientId,
                        });
                      }}
                    />
                  )}
                  <div className="small-title">Patients</div>
                  {value.patients?.map((patientInfo, index) => (
                    <HandoverPatientInfo
                      key={value.patients.length + '-' + index}
                      loading={loadingPatients.includes(patientInfo.id)}
                      info={patientInfo}
                      onChange={(data) => changePatient(index, data)}
                      activePhysicians={activePhysicians}
                      onDelete={() => removePatient(index)}
                      style={{ zIndex: 100 - index }}
                      editable={editable}
                      onClickCommentButton={() => {
                        const section = commentSections.find((it) => it.id === patientInfo.id);
                        setCommentSection(section);
                        navigateToColumn.current?.(1);
                      }}
                      unreadComments={unreadComments[patientInfo.id]}
                    />
                  ))}
                  {editable && (
                    <IconButton
                      className="add"
                      onClick={() => {
                        changePatient(value.patients?.length || 0, {
                          assigned_to_id: value.assigned_to_id,
                        });
                      }}
                    >
                      <Icon width="20px" src="plus-circle" alt="Add Patient" />
                    </IconButton>
                  )}
                  <div className="button-wrapper">
                    <ConfirmationButton
                      name="Archive"
                      className="red mb-2"
                      onClick={onArchive}
                      icon="box-archive"
                      confirmation={{
                        message: 'Are you sure you want to archive this handover?',
                      }}
                      loading={value.archived && loadingSave}
                      disabled={loadingSave}
                      hide={
                        !value.id ||
                        !archivable ||
                        value.archived ||
                        (value.physician_id !== ownId && value.author_id !== ownId)
                      }
                    />
                    <ConfirmationButton
                      name="Delete"
                      className="red mb-2"
                      onClick={onDelete}
                      icon="trash"
                      confirmation={{
                        message: 'Are you sure you want to delete this handover?',
                      }}
                      loading={loadingDelete}
                      hide={!value.id || !editable}
                    />
                    <DefaultButton
                      name="Save"
                      className="blue mb-2"
                      onClick={() => onSave()}
                      icon="floppy-disk"
                      disabled={buttonDisabled}
                      loading={loadingSave}
                      hide={!editable}
                    />
                  </div>
                </div>
                {editable && (
                  <ShiftSchedule
                    userId={ownId}
                    onSelectSlot={onSelectSlot}
                    date={date}
                    setDate={setDate}
                    clinicId={clinicId}
                  />
                )}
              </>
            ),
          },
          !editable && {
            name: 'Comments',
            element: (
              <div className="comments-wrapper">
                <div className="comments-header">
                  <div className="small-title pl-2">Comments</div>
                  <DefaultSelectField
                    className="comment-picker"
                    placeholder=""
                    value={commentSection.name}
                    inputs={commentSections.map((it) => it.name)}
                    onChange={(ev) => {
                      const section = commentSections.find((it) => it.name === ev.target.value);
                      setCommentSection(section);
                    }}
                  />
                </div>
                <Comments
                  title=""
                  value={
                    commentSection.name === GeneralCommentSection
                      ? value
                      : value.patients?.find((it) => it.id === commentSection.id)
                  }
                  saveComment={saveComment(commentSection)}
                  deleteComment={deleteComment(commentSection)}
                  submitting={loadingSave}
                  ownId={ownId}
                  setMessagesEnd={setMessagesEnd}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

const HandoverPatientInfo = ({
  info,
  loading,
  onChange,
  activePhysicians,
  onDelete,
  style,
  editable,
  onClickCommentButton,
  unreadComments,
}) => {
  const patient = useAppSelector((state) => state.patients.byId[info.id]);

  function onChangeWrapper(key, val) {
    onChange(PerformanceUtils.clone({ ...info, [key]: val }));
  }

  return (
    <div className="patient-info row" style={style}>
      {loading && !patient?.id ? (
        <CustomLoadingDataSpinner loading={loading} />
      ) : (
        <PatientSearchField
          onSelect={(patient) => patient?.id && onChangeWrapper('id', patient?.id)}
          patient={patient}
          hideTitle={true}
          disabled={!editable}
        />
      )}
      <DefaultSelectField
        style={{ width: '100%' }}
        name="assigned_to_id"
        label="Assign To"
        value={info.assigned_to_id}
        onChange={(event) => onChangeWrapper('assigned_to_id', event.target.value)}
        disabled={!editable}
        inputElements={activePhysicians.map((physician) => {
          return (
            <MenuItem value={physician.id} key={physician.id}>
              {PhysicianUtils.getName(physician)}
            </MenuItem>
          );
        })}
      />
      <div>
        <span className="small-title">Handover Information</span>
        {editable ? (
          <TextareaAutosize
            name="description"
            value={info.description}
            onChange={(event) => onChangeWrapper('description', event.target.value)}
            style={{ color: 'black' }}
          />
        ) : (
          <pre>{info.description}</pre>
        )}
      </div>
      {editable && (
        <IconButton className="delete" onClick={onDelete}>
          <Icon width="15px" height="15px" src="trash" alt="Remove Patient" className="white" />
        </IconButton>
      )}
      {!editable && (
        <IconButton
          size="small"
          style={{ width: '30px', height: '30px' }}
          className="messages-button-wrapper"
          onClick={onClickCommentButton}
        >
          <UnreadBadge unreadCount={unreadComments} />
          <Icon src="messages" width="20px" />
        </IconButton>
      )}
    </div>
  );
};

/**
 * @param {{
 *  handover: import("generated/api").PatientHandover
 * }} props
 */
const HandoverBadge = ({ handover, onClick }) => {
  const { assigned_to_id, patients = DEFAULT_ARRAY } = handover;

  const allPhysicianIds = _.uniq([
    assigned_to_id,
    ...patients
      .filter(({ assigned_to_id }) => !!assigned_to_id)
      .map(({ assigned_to_id }) => assigned_to_id),
  ]);

  const physicians = useAppSelector((state) => state.physicians.byId);
  const ownId = useAppSelector((state) => state.authentication.user.id);
  const creator = physicians[handover.physician_id];
  const assignedTo = physicians[handover.assigned_to_id];

  const unreadComments = useMemo(() => {
    const generalUnread = TaskUtils.getUnreadComments(handover, ownId);
    const patientUnread = _(handover.patients)
      .filter(
        (patient) =>
          handover.physician_id === ownId ||
          handover.assigned_to_id === ownId ||
          patient.assigned_to_id === ownId,
      )
      .flatMap((it) => it.comments ?? [])
      .sumBy((comment) => !comment.read_by.includes(ownId));
    return generalUnread + patientUnread;
  }, [handover, ownId]);

  const difference =
    moment.unix(handover.date_end).diff(moment.unix(handover.date_start), 'days') + 1;
  const startFormat = CalendarUtils.format(handover.date_start, 'MMMM Do');
  const endFormat = CalendarUtils.format(handover.date_end, 'MMMM Do');

  return (
    <SelectableListItem
      key={handover.id}
      selected={false}
      onClick={onClick}
      item={
        <GenericBadge
          id={handover.id}
          title={`${startFormat} to ${endFormat} (${difference}d)`}
          count={unreadComments}
          info={
            <span>
              {PhysicianUtils.getFirstName(creator)} to {PhysicianUtils.getFirstName(assignedTo)}{' '}
              {allPhysicianIds.length > 1 ? `+ ${allPhysicianIds.length - 1}` : ''}
            </span>
          }
        />
      }
    />
  );
};

export const ActiveHandovers = () => {
  const handovers = useAppSelector((state) => state.shifts.handovers);

  const [openHandoverId, setOpenHandoverId] = useState();
  const openHandover = useMemo(
    () => handovers.find((it) => it.id === openHandoverId),
    [handovers, openHandoverId],
  );
  const ownId = useAppSelector((state) => state.authentication.user.id);

  const myHandovers = useMemo(
    () => ShiftUtils.getRelevantHandovers(handovers, ownId, true),
    [handovers, ownId],
  );

  if (myHandovers.length === 0) return null;

  return (
    <>
      <UncontrolledCollapse
        title={
          <span>
            Handovers{' '}
            <UnreadBadge unreadText={'' + myHandovers.length} style={{ background: '#2693d1' }} />
          </span>
        }
        initialValue={false}
      >
        {myHandovers.map((handover) => (
          <HandoverBadge
            key={handover.id}
            handover={handover}
            onClick={() => setOpenHandoverId(handover.id)}
          />
        ))}
      </UncontrolledCollapse>
      {openHandover ? (
        <HandoverModal handover={openHandover} onClose={() => setOpenHandoverId()} />
      ) : null}
    </>
  );
};

export const HandoverModal = ({ handover, onClose, onPin }) => {
  const ownId = useAppSelector((state) => state.authentication.user.id);
  return !!handover ? (
    <DefaultModal
      className="handover-modal"
      title="Handover Details"
      wrap={true}
      padding={false}
      onPin={onPin}
      content={
        <HandoverDetails
          key={handover.id}
          handover={handover}
          onClose={onClose}
          editable={false}
          archivable={handover?.physician_id === ownId}
        />
      }
      isOpen={!!handover}
      onRequestClose={onClose}
    />
  ) : null;
};
